import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DataBackup } from '../components/DataBackup';
import { CloudSync } from '../components/CloudSync';
import { PDFExport } from '../components/PDFExport';
import { PWAInstructions } from '../components/PWAInstructions';
import { SocialLoginStatus } from '../components/SocialLoginStatus';
import { AccountDeletion } from '../components/AccountDeletion';
import { MissedCheckInReminders } from '../components/MissedCheckInReminders';
import { RelationshipBreakMode } from '../components/RelationshipBreakMode';
import { MilestoneCelebrations } from '../components/MilestoneCelebrations';
import { SubscriptionManagement } from '../components/SubscriptionManagement';
import { ArrowLeft, User, Bell, Lock, HelpCircle, LogOut, BarChart3, FileText, Download, Crown, Package, RefreshCw, Loader2, Users, Shield, AlertTriangle, Bug, Moon, Trash2, MessageSquare, GraduationCap, ArrowRight, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { getErrorLogs } from '../lib/error-utils';
import { safeParseJson } from '../lib/storage-utils';
import { useTheme } from '../lib/theme-provider';
import { analytics } from '../lib/analytics';
import { useAdminRole } from '../lib/use-admin-role';
import { AccountSecurity } from '../components/AccountSecurity';
import { RelationshipModeToggle } from '../components/RelationshipModeToggle';
import { AdaptivePrivacySettings } from '../components/AdaptivePrivacySettings';
import { InfoHint } from '../components/InfoHint';
import { applyPerformanceMode, readPerformanceMode, type PerformanceMode, writePerformanceMode } from '../lib/performance-mode';
import { isNativeConsumerShell } from '../lib/consumer-shell-guards';

export default function Settings() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [purchasedPacks, setPurchasedPacks] = useState<string[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'account' | 'subscription' | 'data' | 'privacy'>('account');
  const [performanceMode, setPerformanceMode] = useState<PerformanceMode>('auto');
  
  // Defensive: handle potential undefined auth during hot reload
  const user = auth?.user || null;
  const accessToken = auth?.accessToken || null;
  const signOut = auth?.signOut || (async () => {});
  const { isAdmin } = useAdminRole();
  const showDeveloperTools = isAdmin && !isNativeConsumerShell();

  useEffect(() => {
    const packs = safeParseJson<string[]>(localStorage.getItem('tension_purchased_packs'), []);
    setPurchasedPacks(Array.isArray(packs) ? packs : []);
  }, []);

  useEffect(() => {
    setPerformanceMode(readPerformanceMode());
  }, []);

  const syncSubscription = async () => {
    if (!accessToken) {
      toast.error('Please sign in to sync subscription');
      return;
    }

    setSyncing(true);
    try {
      if (import.meta.env.DEV) {
        console.log('🔄 Syncing subscription with Stripe verification...', { email: user?.email });
      }
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/verify-payment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-User-Token': accessToken,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`Failed to verify payment: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      if (data.success && data.profile) {
        const currentPlan = localStorage.getItem('tension_subscription') || 'free';
        const currentPacks = JSON.parse(localStorage.getItem('tension_purchased_packs') || '[]');
        let localUpdates = false;
        
        // Sync subscription plan (always update to match server)
        if (data.profile.plan !== currentPlan) {
          localStorage.setItem('tension_subscription', data.profile.plan);
          localUpdates = true;
        }
        
        // Sync purchased packs (always update to match server)
        const serverPacks = data.profile.purchasedPacks || [];
        const serverPacksStr = JSON.stringify(serverPacks);
        const currentPacksStr = JSON.stringify(currentPacks);
        
        if (serverPacksStr !== currentPacksStr) {
          localStorage.setItem('tension_purchased_packs', serverPacksStr);
          setPurchasedPacks(serverPacks);
          localUpdates = true;
        }
        
        if (localUpdates) {
          if (data.updated) {
            toast.success('Found new purchases! Subscription updated.');
          } else {
            toast.success('Subscription synced successfully!');
          }
          setTimeout(() => window.location.reload(), 1000);
        } else {
          if (data.updated) {
            toast.info('Subscription verified (no local changes needed)');
          } else {
            toast.info('Already in sync! No updates found.');
          }
        }
      } else if (data.error) {
        console.error('⚠️  Server error:', data.error);
        toast.error(`Sync failed: ${data.error}`);
      } else {
        if (import.meta.env.DEV) console.warn('Unexpected verification response format', data);
        toast.warning('Unexpected response from server');
      }
    } catch (error) {
      console.error('❌ Sync error:', error);
      toast.error('Failed to sync subscription. Check console for details.');
    } finally {
      setSyncing(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/auth', { replace: true });
    } catch (e) {
      console.error('Sign out error:', e);
      toast.error('Could not sign out. Please try again.');
    }
  };

  const handleExportData = () => {
    // TEMPORARY: Export from local storage
    const storedCheckins = localStorage.getItem('tension_checkins');
    let checkins: unknown[] = [];
    if (storedCheckins) {
      try {
        const parsed = JSON.parse(storedCheckins) as unknown;
        checkins = Array.isArray(parsed) ? parsed : [];
      } catch {
        toast.error('Saved check-in data is corrupted and could not be exported');
        return;
      }
    }
    
    if (checkins.length === 0) {
      toast.error('No data to export yet');
      return;
    }

    // Generate CSV
    const headers = ['Date', 'Time', 'Tension Score (RTI)', 'State', 'Initiation', 'Response', 'Emotional', 'Availability', 'Withdrawal'];
    const rows = checkins.map((raw) => {
      const c = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
      const ts = c.timestamp;
      const t = typeof ts === 'string' || typeof ts === 'number' ? new Date(ts) : new Date(NaN);
      const rti = typeof c.rtiScore === 'number' ? c.rtiScore : Number(c.rtiScore);
      const score = Number.isFinite(rti) ? rti : 0;
      const resp =
        c.responses && typeof c.responses === 'object' && !Array.isArray(c.responses)
          ? (c.responses as Record<string, unknown>)
          : {};
      const cell = (v: unknown) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');
      return [
        Number.isNaN(t.getTime()) ? '' : t.toLocaleDateString(),
        Number.isNaN(t.getTime()) ? '' : t.toLocaleTimeString(),
        score,
        score <= 35 ? 'Stable' : score <= 60 ? 'Elastic' : 'High Tension',
        cell(resp.initiation),
        cell(resp.response),
        cell(resp.emotional),
        cell(resp.availability),
        cell(resp.withdrawal),
      ];
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tension-data-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Data exported successfully');
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    analytics.track('theme_changed', { theme: newTheme });
  };

  const handlePerformanceModeChange = (nextMode: PerformanceMode) => {
    setPerformanceMode(nextMode);
    writePerformanceMode(nextMode);
    applyPerformanceMode(nextMode);
    analytics.track('performance_mode_changed', { mode: nextMode, surface: 'settings' });
    toast.success(nextMode === 'minimal' ? 'Performance mode enabled' : 'Adaptive performance enabled');
  };

  // Check subscription status
  const subscription = localStorage.getItem('tension_subscription') || 'free';

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white/98 px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.08)] backdrop-blur-[6px] sm:px-6">
        <div className="max-w-2xl mx-auto flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/home')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-700" />
          </button>
          <h1 className="text-lg sm:text-xl font-light text-neutral-900">Settings</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-5 sm:space-y-6">
        {/* Account */}
        <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Account</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                <User className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {typeof user?.user_metadata?.name === 'string' ? user.user_metadata.name : 'User'}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    {typeof user?.email === 'string' ? user.email : ''}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/user-profile')}
                >
                  My Profile
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <AccountSecurity />

        {/* Relationship mode (couples vs singles) */}
        <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
          <div className="space-y-4">
            <h2 className="text-lg font-medium inline-flex items-center gap-1.5">
              Relationship mode
              <InfoHint
                title="Relationship mode"
                description="Switch between couples view and singles view. This changes your home tools and guidance style."
              />
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Choose couples mode for shared relationship tracking, or singles mode for personal growth and readiness.
              You can switch any time.
            </p>
            <RelationshipModeToggle />
          </div>
        </Card>

        {/* Relationships */}
        <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Relationships</h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/relationships')}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 rounded-lg transition-colors"
              >
                <Users className="w-5 h-5 text-neutral-600" />
                <div className="flex-1 text-left">
                  <p className="text-sm">Manage Relationships</p>
                  <p className="text-xs text-neutral-500">
                    {subscription === 'free' && 'Track up to 2 relationships'}
                    {subscription === 'premium' && 'Track up to 3 relationships'}
                    {subscription === 'pro' && 'Track up to 5 relationships'}
                  </p>
                </div>
              </button>
            </div>
          </div>
        </Card>

        {/* Growth & Rewards */}
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50/95 to-pink-50/95 p-6 dark:border-purple-900/45 dark:from-purple-950/35 dark:to-pink-950/20">
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-medium text-purple-900">Growth & Rewards</h2>
              <p className="text-xs text-purple-700 mt-1">
                Earn rewards by inviting friends and sharing TENSION
              </p>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/referrals')}
                className="w-full flex items-center gap-3 p-3 bg-white hover:bg-purple-50 rounded-lg transition-colors border border-purple-200"
              >
                <Users className="w-5 h-5 text-purple-600" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-purple-900">Invite Friends</p>
                  <p className="text-xs text-purple-700">
                    Get 1 month free Pro for every 3 active referrals
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-purple-600" />
              </button>
              <button
                onClick={() => navigate('/affiliate-partner')}
                className="w-full flex items-center gap-3 p-3 bg-white hover:bg-purple-50 rounded-lg transition-colors border border-purple-200"
              >
                <DollarSign className="w-5 h-5 text-green-600" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-purple-900">Partner Program</p>
                  <p className="text-xs text-purple-700">
                    Earn 30% recurring commission by sharing TENSION
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-purple-600" />
              </button>
            </div>
          </div>
        </Card>

        {/* Analytics */}
        <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
          <div className="space-y-4">
            <h2 className="text-lg font-medium inline-flex items-center gap-1.5">
              Your Data
              <InfoHint
                title="Your Data"
                description="View trends, see weekly summaries, and export your records when you want a backup."
              />
            </h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/timeline')}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 rounded-lg transition-colors"
              >
                <BarChart3 className="w-5 h-5 text-neutral-600" />
                <span className="flex-1 text-left text-sm">Tension Timeline</span>
              </button>
              <button
                onClick={() => navigate('/summary')}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 rounded-lg transition-colors"
              >
                <FileText className="w-5 h-5 text-neutral-600" />
                <span className="flex-1 text-left text-sm">Weekly Summary</span>
              </button>
              <button
                onClick={handleExportData}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 rounded-lg transition-colors"
              >
                <Download className="w-5 h-5 text-neutral-600" />
                <span className="flex-1 text-left text-sm">Export CSV</span>
              </button>
            </div>
          </div>
        </Card>

        {/* PDF Export for Therapists */}
        <PDFExport />

        {/* Cloud Sync */}
        <CloudSync />

        {/* Data Backup & Management */}
        <DataBackup />

        {/* Preferences */}
        <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Preferences</h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/notification-settings')}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <Bell className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                <span className="flex-1 text-left text-sm">Notifications</span>
              </button>
              
              {/* Theme Selection */}
              <div className="p-3 space-y-3">
                <div className="flex items-center gap-3">
                  <Moon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                  <span className="flex-1 text-left text-sm">Appearance</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pl-0 sm:pl-8">
                  <button
                    onClick={() => handleThemeChange('light')}
                    className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                      theme === 'light'
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                    }`}
                  >
                    Light
                  </button>
                  <button
                    onClick={() => handleThemeChange('dark')}
                    className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                      theme === 'dark'
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                    }`}
                  >
                    Dark
                  </button>
                  <button
                    onClick={() => handleThemeChange('system')}
                    className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                      theme === 'system'
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                    }`}
                  >
                    System
                  </button>
                </div>
              </div>

              <div className="p-3 space-y-3 border-t border-neutral-100 dark:border-neutral-800">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                  <div className="flex-1">
                    <span className="text-left text-sm">Performance mode</span>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      Minimal reduces motion effects for battery life and smoother mobile performance.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-0 sm:pl-8">
                  <button
                    onClick={() => handlePerformanceModeChange('auto')}
                    className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                      performanceMode === 'auto'
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                    }`}
                  >
                    Adaptive (recommended)
                  </button>
                  <button
                    onClick={() => handlePerformanceModeChange('minimal')}
                    className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                      performanceMode === 'minimal'
                        ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                    }`}
                  >
                    Minimal motion
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* PWA Installation */}
        <PWAInstructions />

        {/* Subscription Management */}
        <SubscriptionManagement />

        {/* Purchased Packs */}
        <Card className="border-violet-200/70 bg-white/92 p-6 dark:border-violet-900/45 dark:bg-neutral-900/80">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">Situational Packs</h2>
              <Package className="w-5 h-5 text-neutral-600" />
            </div>
            {purchasedPacks.length > 0 ? (
              <div className="space-y-2">
                {purchasedPacks.map((packId) => {
                  const packNames: Record<string, string> = {
                    'breakup-recovery': 'Breakup Recovery',
                    reconnection: 'Reconnection',
                    'conflict-repair': 'Conflict Repair',
                    'long-distance': 'Long Distance',
                    'anxious-attachment': 'Anxious Attachment',
                    'meeting-family': 'Meeting the Family',
                    'moving-in': 'Moving In Together',
                    'dating-strategy': 'Connection Strategy Pack',
                    'profile-optimization': 'Profile Optimization Pack',
                    'red-flags-assessment': 'Red Flags Assessment',
                    'first-date-guide': 'First Connection Guide',
                    'compatibility-deep-dive': 'Alignment Deep Dive',
                    'breakup-prevention': 'Early Relationship Success',
                    'singles-bundle': 'Complete Singles Bundle',
                  };
                  return (
                    <button
                      key={packId}
                      onClick={() => navigate(`/pack/${packId}`)}
                      className="tension-glow w-full flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg hover:border-purple-300 transition-colors"
                    >
                      <span className="text-sm font-medium text-neutral-900">
                        {packNames[packId] ?? packId.replace(/-/g, ' ')}
                      </span>
                      <span className="text-xs text-green-600">Owned</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 bg-neutral-50 rounded-lg text-center">
                <p className="text-sm text-neutral-600 mb-3">
                  No packs purchased yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/packs')}
                >
                  Browse Packs - $2.99 each
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Developer Tools — admin only */}
        {showDeveloperTools && (
          <Card className="p-6 bg-neutral-50 border-neutral-300 dark:bg-neutral-900/50 dark:border-neutral-700">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-neutral-600" />
                <h2 className="text-lg font-medium">Developer Tools</h2>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/error-logs')}
                  className="w-full flex items-center justify-between gap-3 p-3 hover:bg-white dark:hover:bg-neutral-800 rounded-lg transition-colors border border-neutral-200 dark:border-neutral-700"
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    <div className="text-left">
                      <p className="text-sm">Error Logs</p>
                      <p className="text-xs text-neutral-600">{getErrorLogs().length} errors logged</p>
                    </div>
                  </div>
                  {getErrorLogs().length > 0 && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                      {getErrorLogs().length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => navigate('/debug')}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white dark:hover:bg-neutral-800 rounded-lg transition-colors border border-neutral-200 dark:border-neutral-700"
                >
                  <Bug className="w-5 h-5 text-neutral-600" />
                  <span className="flex-1 text-left text-sm">Debug Console</span>
                </button>
                <button
                  onClick={() => navigate('/admin-demo')}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white dark:hover:bg-neutral-800 rounded-lg transition-colors border border-neutral-200 dark:border-neutral-700 bg-purple-50 dark:bg-purple-900/20"
                >
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  <div className="text-left flex-1">
                    <p className="text-sm font-medium text-purple-900 dark:text-purple-200">Admin Dashboard</p>
                    <p className="text-xs text-purple-700 dark:text-purple-400">View analytics & metrics</p>
                  </div>
                </button>
              </div>
              <div className="tension-glow p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  ℹ️ These tools help diagnose issues. Error logs are stored locally and never sent to servers.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Social Login Configuration Status */}
        <SocialLoginStatus />

        {/* Milestones & Achievements */}
        <MilestoneCelebrations />

        {/* Check-In Reminders */}
        <MissedCheckInReminders />

        {/* Relationship Break Mode */}
        <RelationshipBreakMode />

        <AdaptivePrivacySettings />

        {/* Privacy & Legal */}
        <Card className="p-6">
          <div className="space-y-4">
            <h2 className="text-lg font-medium inline-flex items-center gap-1.5">
              Help & Legal
              <InfoHint
                title="Help & Legal"
                description="Find quick help, contact support, and review privacy and policy pages."
              />
            </h2>
            <div className="space-y-3">
              <button 
                onClick={() => navigate('/faq')}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <HelpCircle className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                <span className="flex-1 text-left text-sm font-medium">FAQ</span>
              </button>
              <button
                onClick={() => navigate('/help/sync')}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <RefreshCw className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-400" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Sync & personalization help</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Web and mobile app</p>
                </div>
              </button>
              <button 
                onClick={() => navigate('/feedback')}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <MessageSquare className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Report Bug / Give Feedback</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Help us improve TENSION</p>
                </div>
              </button>
              <button 
                onClick={() => navigate('/support')}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="flex-1 text-left text-sm font-medium">Contact Support</span>
              </button>
              <button 
                onClick={() => {
                  // Clear onboarding flags to trigger tutorial
                  localStorage.removeItem('onboarding_completed');
                  localStorage.removeItem('onboarding_skipped');
                  localStorage.removeItem('onboarding_completed_at');
                  localStorage.removeItem('onboarding_skipped_at');
                  toast.success('Tutorial reset! Reload the page to see it.');
                  setTimeout(() => window.location.reload(), 1500);
                }}
                className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <GraduationCap className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Restart Tutorial</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">View the welcome tour again</p>
                </div>
              </button>
              <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3 mt-3">
                <button 
                  onClick={() => navigate('/privacy')}
                  className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                >
                  <Lock className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                  <span className="flex-1 text-left text-sm">Privacy Policy</span>
                </button>
                <button 
                  onClick={() => navigate('/terms')}
                  className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                >
                  <Shield className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                  <span className="flex-1 text-left text-sm">Terms of Service</span>
                </button>
                <button 
                  onClick={() => navigate('/about')}
                  className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                >
                  <HelpCircle className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                  <span className="flex-1 text-left text-sm">About TENSION</span>
                </button>
              </div>
            </div>
            <div className="p-4 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
              <p className="text-xs text-neutral-600 dark:text-neutral-400">
                TENSION does not access your messages. All data is user-controlled and private.
                This app does not provide therapy, counseling, or medical advice.
              </p>
            </div>
          </div>
        </Card>

        {/* Delete Account */}
        <Card className="border-red-200 bg-white/92 p-6 dark:border-red-800 dark:bg-neutral-900/80">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              <h2 className="text-lg font-medium text-red-900 dark:text-red-100">Delete Account</h2>
            </div>

            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Permanently delete your TENSION account and all associated data. This action cannot be undone.
            </p>

            <div className="tension-glow bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-900 dark:text-red-100 font-medium mb-2">
                ⚠️ This will permanently delete:
              </p>
              <ul className="text-xs text-red-800 dark:text-red-200 space-y-1">
                <li>• Your account and authentication credentials</li>
                <li>• All relationship data and check-ins</li>
                <li>• Journal entries and anxiety logs</li>
                <li>• All insights and assessments</li>
                <li>• Subscription and payment history</li>
                <li>• Purchased situational packs</li>
              </ul>
            </div>

            <Button
              onClick={async () => {
                const confirmed = window.confirm(
                  'Are you absolutely sure you want to delete your account?\n\n' +
                  'This will:\n' +
                  '• Delete ALL your data permanently\n' +
                  '• Sign you out immediately\n' +
                  '• Cancel any active subscriptions\n\n' +
                  'This action CANNOT be undone.\n\n' +
                  'Type DELETE in the next prompt to confirm.'
                );
                
                if (!confirmed) return;
                
                const confirmText = prompt('Type DELETE to confirm account deletion:');
                
                if (confirmText !== 'DELETE') {
                  toast.error('Account deletion cancelled. Text did not match.');
                  return;
                }
                
                // Delete all local data
                const keysToRemove = [
                  'tension_relationships',
                  'tension_active_relationship',
                  'tension_checkins',
                  'tension_journal_entries',
                  'tension_anxiety_tracker',
                  'tension_attachment_assessments',
                  'tension_rumination_interventions',
                  'tension_situational_packs',
                  'tension_subscription',
                  'tension_onboarding_complete',
                  'tension_notification_settings',
                  'tension_notifications_enabled',
                  'tension_daily_reminder',
                ];
                
                keysToRemove.forEach((key) => localStorage.removeItem(key));
                
                // Delete Supabase account
                analytics.track('account_deleted', {});
                
                // Sign out and redirect
                await signOut();
                toast.success('Account deleted successfully');
                setTimeout(() => {
                  window.location.href = '/';
                }, 1000);
              }}
              variant="outline"
              className="w-full border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account Forever
            </Button>

            <p className="text-xs text-neutral-600 dark:text-neutral-400 italic">
              Tip: Export your data before deleting if you want to keep a backup.
            </p>
          </div>
        </Card>

        {/* Sign Out */}
        <Button
          onClick={handleSignOut}
          variant="outline"
          className="w-full text-red-600 border-red-200 hover:bg-red-50"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>

        <p className="text-xs text-center text-neutral-500">
          TENSION v1.0 • Made with restraint
        </p>
      </div>
    </div>
  );
}